import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ModuleIntroModal } from "../../module/ModuleIntroModal";
import { ModuleStudioShell } from "../../module/ModuleStudioShell";
import { GymCountdownOverlay, type GymCountdownPhase } from "../../gym/GymCountdownOverlay";
import { PracticeSurface } from "../../practice/PracticeSurface";
import { Button } from "../../ui/button";
import { CHORD_HUB_THEME as T } from "../../../lib/chords/chordHubTheme";
import type { ChordInversionsHubCard } from "../../../lib/chords/chordHubTypes";
import {
  generateExplorerVoicings,
  getChordDef,
  type ExplorerFilter,
  type InversionType,
} from "../../../lib/chordEngine";
import { inversionOptionsForChord } from "../../../lib/chordStaffEvents";
import { useInstrument } from "../../../contexts/InstrumentContext";

const COUNTDOWN_TICK_MS = 880;
const COUNTDOWN_GO_MS = 1_000;

const INVERSION_LABELS: Record<InversionType, string> = {
  fundamental: "Fundamental",
  first: "1ª inversión",
  second: "2ª inversión",
  third: "3ª inversión",
};

const BASE_FILTER: ExplorerFilter = {
  selectedStrings: [5, 4, 3, 2, 1, 0],
  allowOpenStrings: true,
  allowBarre: true,
  maxFret: 15,
  maxSpan: 5,
  inversion: "fundamental",
  voicingType: "all",
};

type StudioPhase = "intro" | "countdown" | "browse" | "complete";

export interface ChordInversionsStudioProps {
  card: ChordInversionsHubCard;
  wasCompletedBefore: boolean;
  onBack: () => void;
  onComplete: () => void;
}

export function ChordInversionsStudio({
  card,
  wasCompletedBefore,
  onBack,
  onComplete,
}: ChordInversionsStudioProps) {
  const { surface } = useInstrument();

  const [phase, setPhase] = useState<StudioPhase>("intro");
  const [countdown, setCountdown] = useState<GymCountdownPhase | null>(null);
  const [inversionIndex, setInversionIndex] = useState(0);
  const [seenInversions, setSeenInversions] = useState<Set<number>>(() => new Set([0]));

  const inversionSteps = useMemo(() => {
    const def = getChordDef(card.payload.quality);
    const options = inversionOptionsForChord(def?.intervals.length ?? 3);
    return options
      .map((inv) => {
        const voicings = generateExplorerVoicings(card.payload.root, card.payload.quality, {
          ...BASE_FILTER,
          inversion: inv,
        });
        return { inversion: inv, voicing: voicings[0] ?? null };
      })
      .filter((step) => step.voicing !== null);
  }, [card.payload]);

  const currentStep = inversionSteps[inversionIndex] ?? null;
  const def = getChordDef(card.payload.quality);

  const mutedStrings = useMemo(() => currentStep?.voicing?.mutedStrings ?? [], [currentStep]);

  const highlightedPositions = useMemo(() => {
    if (!currentStep?.voicing) return [];
    return currentStep.voicing.positions.filter((p) => !mutedStrings.includes(p.string));
  }, [currentStep, mutedStrings]);

  const allSeen =
    inversionSteps.length > 0 && seenInversions.size >= inversionSteps.length;

  const handleBeginCountdown = useCallback(() => {
    setCountdown(3);
  }, []);

  useEffect(() => {
    if (countdown === null) return;
    if (countdown === "go") {
      const id = window.setTimeout(() => {
        setPhase("browse");
        setCountdown(null);
      }, COUNTDOWN_GO_MS);
      return () => window.clearTimeout(id);
    }
    const id = window.setTimeout(() => {
      if (typeof countdown === "number") {
        if (countdown <= 1) setCountdown("go");
        else setCountdown(countdown - 1);
      }
    }, COUNTDOWN_TICK_MS);
    return () => window.clearTimeout(id);
  }, [countdown]);

  const goToInversion = useCallback(
    (next: number) => {
      if (inversionSteps.length === 0) return;
      const idx = ((next % inversionSteps.length) + inversionSteps.length) % inversionSteps.length;
      setInversionIndex(idx);
      setSeenInversions((prev) => new Set(prev).add(idx));
    },
    [inversionSteps.length]
  );

  const handleFinish = useCallback(() => {
    setPhase("complete");
    window.setTimeout(() => {
      onComplete();
    }, 900);
  }, [onComplete]);

  const dimNeck = phase === "intro" || countdown !== null;
  const promptText =
    inversionSteps.length > 0 && currentStep
      ? `${INVERSION_LABELS[currentStep.inversion]} (${inversionIndex + 1}/${inversionSteps.length})`
      : "Sin inversiones disponibles";

  if (surface === "keyboard") {
    return (
      <ModuleStudioShell
        title={card.title}
        subtitle={card.subtitle}
        onBack={onBack}
        accent={T.accent}
        border={T.border}
        textMain={T.textMain}
        textMuted={T.textMuted}
        textDim={T.textDim}
      >
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <p className="text-sm max-w-md" style={{ color: T.textMuted }}>
            Esta lección está optimizada para guitarra. Cambia el instrumento de sesión o abre el
            explorador libre para piano.
          </p>
          <Button onClick={onBack} className="mt-4" variant="outline">
            Volver al hub
          </Button>
        </div>
      </ModuleStudioShell>
    );
  }

  return (
    <ModuleStudioShell
      title={card.title}
      subtitle={card.subtitle}
      onBack={onBack}
      accent={T.accent}
      border={T.border}
      textMain={T.textMain}
      textMuted={T.textMuted}
      textDim={T.textDim}
      headerExtra={
        phase === "browse" && inversionSteps.length > 0 ? (
          <span className="text-[10px] tabular-nums" style={{ color: T.textDim }}>
            {seenInversions.size}/{inversionSteps.length} vistas
          </span>
        ) : undefined
      }
    >
      <div className="flex-1 min-h-[min(62vh,560px)] flex items-center justify-center p-2 sm:p-4 relative rounded-2xl overflow-hidden">
        <div
          className="relative w-full h-full max-w-[min(96vw,1100px)] mx-auto flex items-center justify-center transition-opacity duration-500 ease-out"
          style={{ opacity: dimNeck ? 0.35 : 1 }}
        >
          {currentStep?.voicing ? (
            <PracticeSurface
              className="h-full w-full"
              highlightedPositions={highlightedPositions}
              mutedStrings={mutedStrings}
              barre={currentStep.voicing.barre ?? null}
              showNoteNames
              showDegrees
              visualStyle="lutherie"
            />
          ) : (
            <p className="text-sm" style={{ color: T.textMuted }}>
              No hay inversiones disponibles para este acorde en guitarra.
            </p>
          )}
        </div>

        {phase === "intro" && (
          <ModuleIntroModal
            title={card.title}
            subtitle={card.subtitle}
            description={card.description}
            instructions={card.instructions}
            tags={card.tags}
            timeEstimateMin={card.timeEstimateMin}
            accent={T.accent}
            accentSoft={T.accentSoft}
            accentGlow={T.accentGlow}
            borderHover={T.borderHover}
            textMain={T.textMain}
            textMuted={T.textMuted}
            textDim={T.textDim}
            wasCompletedBefore={wasCompletedBefore}
            onBegin={handleBeginCountdown}
          />
        )}

        {countdown !== null && <GymCountdownOverlay phase={countdown} />}

        {phase === "browse" && (
          <>
            <div className="absolute top-3 left-1/2 z-30 w-[min(92%,420px)] -translate-x-1/2 pointer-events-none">
              <div
                className="flex items-center gap-3 px-4 py-3 rounded-2xl"
                style={{
                  background: "rgba(28,26,25,0.9)",
                  border: `1px solid ${T.borderHover}`,
                  boxShadow: "0 12px 40px rgba(0,0,0,0.45)",
                }}
              >
                <span
                  className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                  style={{ backgroundColor: T.accentSoft, color: T.accent }}
                >
                  {inversionIndex + 1}
                </span>
                <p className="text-sm font-medium flex-1" style={{ color: T.textMain }}>
                  {promptText}
                  {def && (
                    <span className="block text-[11px] font-normal mt-0.5" style={{ color: T.textDim }}>
                      {def.degreeNames.join(" · ")}
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="absolute bottom-4 left-1/2 z-30 -translate-x-1/2 flex flex-wrap items-center justify-center gap-2 pointer-events-auto px-2">
              <button
                type="button"
                onClick={() => goToInversion(inversionIndex - 1)}
                className="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-white/10"
                style={{ border: `1px solid ${T.border}`, color: T.textMuted }}
                aria-label="Inversión anterior"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <Button
                onClick={handleFinish}
                disabled={!allSeen}
                className="font-semibold text-white border-0 px-5"
                style={{
                  backgroundColor: allSeen ? T.accent : "rgba(255,255,255,0.12)",
                  opacity: allSeen ? 1 : 0.7,
                }}
              >
                {allSeen ? "Completar lección" : `Ver ${inversionSteps.length - seenInversions.size} más`}
              </Button>
              <button
                type="button"
                onClick={() => goToInversion(inversionIndex + 1)}
                className="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-white/10"
                style={{ border: `1px solid ${T.border}`, color: T.textMuted }}
                aria-label="Inversión siguiente"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </>
        )}

        {phase === "complete" && (
          <div className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none">
            <div
              className="rounded-2xl px-8 py-6 text-center module-complete-pop"
              style={{
                background: "rgba(28,26,25,0.94)",
                border: `1px solid ${T.accent}`,
                boxShadow: `0 0 40px ${T.accentGlow}`,
              }}
            >
              <p className="text-lg font-bold" style={{ color: T.textMain }}>
                ¡Lección completada!
              </p>
              <p className="text-xs mt-1" style={{ color: T.textDim }}>
                Volviendo al hub…
              </p>
            </div>
            <style>{`
              .module-complete-pop { animation: moduleCompletePop 0.5s ease-out both; }
              @keyframes moduleCompletePop {
                from { opacity: 0; transform: scale(0.92); }
                to { opacity: 1; transform: scale(1); }
              }
            `}</style>
          </div>
        )}
      </div>
    </ModuleStudioShell>
  );
}
